import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  IconButton,
  List,
  ListIcon,
  ListItem,
  Skeleton,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdError, MdLink } from 'react-icons/md';
import { useForm, Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useMemo, useRef, useState, useCallback, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { BotTag, ChatbotFormData, Task, TaskAction, WorkflowLinks, getTargetFromLink } from '@/lib/sdk/chatbot/types';
import { FormField } from '@/components/Modal/FormField';
import { LinksManager } from '@/components/Chatbot/LinksManager';
import { useChatbotLinks, useChatbots } from '@/lib/sdk/chatbot/queries';
import { useLocalStorage, STORAGE_KEYS } from '../hooks/useLocalStorage';
import { WorkflowTaskGrid, WorkflowTaskGridHandle } from './WorkflowTaskGrid';
import { GridRow, gridRowToTask } from './useTaskGridReducer';
import { TaskConfigPanel } from './TaskConfigPanel';
import { toPanelTask } from './valueRefTasks';
import { SubtaskEditorModal } from './SubtaskEditorModal';

// ─── Schema (identical to ChatbotForm) ───────────────────────────────────────

const formSchema = z.object({
  title: z.string().min(1, 'Title is required').max(55, 'Title must be 55 characters or less'),
  template: z.string().min(1, 'Template is required'),
  task_lifecycle: z.enum(['daily', 'long_running', 'weekly', 'monthly']).default('daily'),
  ttl_unit: z.enum(['hours', 'days']).default('hours'),
  ttl: z.number().min(1, 'TTL is required').optional(),
  tags: z.array(z.object({ key: z.string().min(1, 'Key is required'), value: z.string().min(1, 'Value is required') })),
  tasks: z.array(z.object({
    id: z.string(),
    name: z.string().min(1, 'Task name is required').refine((name) => name.length <= 24, 'Task name must be 24 characters or less'),
    description: z.string().max(1024).optional().default(""),
    type: z.string(),
    data: z.string().optional(),
    actions: z.array(z.string()).optional(),
    mutable: z.boolean().optional(),
  })),
}).superRefine((data, ctx) => {
  const needsTtl = data.task_lifecycle === 'daily' || data.task_lifecycle === 'long_running';
  if (needsTtl && (data.ttl == null || data.ttl < 1)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'TTL is required', path: ['ttl'] });
  }
  if (data.task_lifecycle === 'daily' && data.ttl != null && data.ttl > 72) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'TTL must be 72 hours or less for daily tasks', path: ['ttl'] });
  }
  if (data.task_lifecycle === 'long_running' && data.ttl_unit === 'days' && data.ttl != null && data.ttl > 365) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'TTL must be 365 days or less', path: ['ttl'] });
  }
  if (data.task_lifecycle === 'long_running' && data.ttl_unit === 'hours' && data.ttl != null && data.ttl > 8760) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'TTL must be 8760 hours or less', path: ['ttl'] });
  }
});

// ─── Props (same interface as ChatbotForm) ────────────────────────────────────

interface ChatbotFormProps {
  onSubmit: (data: ChatbotFormData) => void;
  initialData?: ChatbotFormData;
  isLoading?: boolean;
  chatbotCode?: string;
  orgId?: string;
  /** Read-only context (not part of ChatbotFormData/the save payload) — threaded
   * through to TaskConfigPanel so dynamic_choice fields can allow up to 200 results
   * (a single scrollable Dropdown) once Flows are enabled, vs. 50 on the native
   * channel (paginated list messages, sent in pages of 10). */
  flowsEnabled?: boolean;
  /** Links to start a new workflow with (the AI assistant's draft); create only */
  initialLinks?: WorkflowLinks;
  /** Ids of tasks the AI assistant changed, highlighted in the grid */
  highlightTaskIds?: ReadonlySet<string>;
  /** Extra buttons for the task grid's toolbar, such as Edit with AI */
  taskToolbarActions?: ReactNode;
}

const VALIDATE_TRIGGER_ID = 'workflow-task-grid-v4-validate';
const GRID_ROWS_STORAGE_KEY = STORAGE_KEYS.FORM_DATA + '_v4_rows';

const templateOptions = [
  { value: 'sop', label: 'SOP' },
  { value: 'form', label: 'Form' },
];
const lifecycleOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'long_running', label: 'Long Running' },
  { value: 'weekly', label: 'Weekly Recurring' },
  { value: 'monthly', label: 'Monthly Recurring' },
];
const ttlUnitOptions = [
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export const ChatbotFormV4 = ({
  onSubmit,
  initialData,
  isLoading = false,
  chatbotCode,
  orgId,
  flowsEnabled,
  initialLinks,
  highlightTaskIds,
  taskToolbarActions,
}: ChatbotFormProps) => {
  const toast = useToast();
  const { state } = useLocation();
  const [pendingTasks, setPendingTasks] = useState<Task[] | null>(null);
  const pendingTasksRef = useRef<Task[] | null>(null);
  const gridRef = useRef<WorkflowTaskGridHandle>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelRow, setPanelRow] = useState<GridRow | null>(null);
  const [panelRowIdx, setPanelRowIdx] = useState(0);
  const [subtaskEditorOpen, setSubtaskEditorOpen] = useState(false);
  const [subtaskEditorRow, setSubtaskEditorRow] = useState<GridRow | null>(null);
  const { isOpen: isLinksOpen, onOpen: onLinksOpen, onClose: onLinksClose } = useDisclosure();
  const { isOpen: isErrorDialogOpen, onOpen: onErrorDialogOpen, onClose: onErrorDialogClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [tags, setTags] = useState<BotTag[]>(initialData?.tags || []);
  const [pendingLinks, setPendingLinks] = useState<WorkflowLinks>(initialLinks ?? state?.linkedWorkflows ?? {});
  const [gridErrors, setGridErrors] = useState(0);

  const { getItem, setItem, removeItem } = useLocalStorage<ChatbotFormData>(
    STORAGE_KEYS.FORM_DATA + '_v4',
    { persistAcrossSessions: false }
  );

  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const form = useForm<ChatbotFormData>({
    resolver: zodResolver(formSchema) as unknown as Resolver<ChatbotFormData>,
    defaultValues: initialData || {
      title: state?.importedTitle ?? '',
      template: state?.importedTemplate ?? '',
      tasks: [],
      ttl: 24,
      task_lifecycle: 'daily',
      ttl_unit: 'hours',
      tags: [],
    },
  });

  const watchLifecycle = form.watch('task_lifecycle');
  const watchTtlUnit = form.watch('ttl_unit');
  const watchTemplate = form.watch('template');

  const { data: linksData, isLoading: isLoadingLinks } = useChatbotLinks(chatbotCode || '');
  const currentLinksCount = linksData?.links ? Object.keys(linksData.links).length : 0;

  // Real workflows in this org — used to constrain STARTNEW's Static Target
  // Chatbot to an existing workflow instead of free text.
  const { data: chatbotsData } = useChatbots({ orgId: orgId || '', limit: 100, active_only: true });
  const availableWorkflows = useMemo(
    () =>
      (chatbotsData?.items ?? [])
        .filter((chatbot) => chatbot.template === 'form')
        .map((chatbot) => ({ short_code: chatbot.short_code, title: chatbot.title })),
    [chatbotsData]
  );

  const defaultTargetKey = useMemo(() => {
    const tasks = initialData?.tasks ?? [];
    const startnewTask = tasks.find((t) => t.actions?.includes(TaskAction.STARTNEW));
    return startnewTask?.startnewConfig?.defaultTargetKey;
  }, [initialData?.tasks]);

  // Restore grid rows from localStorage on first mount (create mode only)
  const initialGridTasks = useMemo<Task[] | undefined>(() => {
    if (initialData?.tasks?.length) return undefined;
    if (state?.copiedTasks?.length) return undefined;
    try {
      const raw = localStorage.getItem(GRID_ROWS_STORAGE_KEY);
      if (!raw) return undefined;
      const rows: GridRow[] = JSON.parse(raw);
      return rows.map((r) => gridRowToTask(r));
    } catch {
      return undefined;
    }
  }, []); // intentionally empty — runs once on mount

  const handleRowsChange = useCallback((rows: GridRow[]) => {
    if (!initialData) {
      localStorage.setItem(GRID_ROWS_STORAGE_KEY, JSON.stringify(rows));
    }
  }, [initialData]);

  // Load localStorage draft on mount (create mode only)
  useEffect(() => {
    if (!initialData) {
      const savedData = getItem();
      if (savedData) {
        form.reset(savedData);
      }
    }
  }, []);

  // Save draft to localStorage on every change (create mode only)
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (!initialData) {
        setItem(value as ChatbotFormData);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  useEffect(() => {
    return () => {
      if (!initialData) {
        removeItem();
        localStorage.removeItem(GRID_ROWS_STORAGE_KEY);
      }
    };
  }, [removeItem, initialData]);

  // Grid valid callback — stash tasks then proceed with form submit
  const handleGridValidRows = (tasks: Task[]) => {
    pendingTasksRef.current = tasks;
    setPendingTasks(tasks);
    form.setValue('tasks', tasks as any);
    form.handleSubmit(handleFormSubmit, onInvalid)();
  };

  const handleGridValidationError = (count: number) => {
    setGridErrors(count);
    toast({
      title: `Fix ${count} task error${count !== 1 ? 's' : ''} before saving`,
      status: 'error',
      duration: 3000,
    });
  };

  const handleFormSubmit = (data: ChatbotFormData) => {
    const tasks = (pendingTasksRef.current ?? []).map((task) => ({
      ...task,
      description: task.description ?? '',
    }));
    const formData: ChatbotFormData = {
      ...data,
      tasks,
      tags,
      task_lifecycle: data.task_lifecycle || 'daily',
      ttl_unit: data.ttl_unit || 'hours',
    };
    if (!chatbotCode && Object.keys(pendingLinks).length > 0) {
      formData.pendingLinks = pendingLinks;
    }
    onSubmit(formData);
  };

  const onInvalid = (errors: any) => {
    const messages: string[] = [];
    const processErrors = (errs: any) => {
      Object.entries(errs).forEach(([key, value]: [string, any]) => {
        if (key === 'tasks' && Array.isArray(value)) {
          value.forEach((taskError: any, index: number) => {
            if (taskError) {
              Object.entries(taskError).forEach(([field, error]: [string, any]) => {
                messages.push(`Task ${index + 1}: ${error?.message || 'Invalid field'} (${field})`);
              });
            }
          });
        } else {
          messages.push(value?.message || `Invalid field: ${key}`);
        }
      });
    };
    processErrors(errors);
    setValidationErrors(messages);
    onErrorDialogOpen();
  };

  // Form submit button triggers grid validation first
  const handleSubmitClick = () => {
    setGridErrors(0);
    document.getElementById(VALIDATE_TRIGGER_ID)?.click();
  };

  const handleAddTag = () => {
    const newTags = [...tags, { key: '', value: '' }];
    setTags(newTags);
    form.setValue('tags', newTags);
  };

  const handleRemoveTag = (index: number) => {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
    form.setValue('tags', newTags);
  };

  const handleTagChange = (index: number, field: 'key' | 'value', value: string | number) => {
    const newTags = [...tags];
    newTags[index][field] = value as string;
    setTags(newTags);
    form.setValue('tags', newTags);
  };

  const handleOpenConfigPanel = useCallback((row: GridRow, rowIdx: number) => {
    setPanelRow(row);
    setPanelRowIdx(rowIdx);
    setPanelOpen(true);
  }, []);

  const handlePanelSave = useCallback((localId: string, config: Partial<GridRow>) => {
    gridRef.current?.dispatchAdvanced(localId, config);
  }, []);

  const handleOpenSubtaskEditor = useCallback((row: GridRow) => {
    setSubtaskEditorRow(row);
    setSubtaskEditorOpen(true);
  }, []);

  const handleSubtaskEditorSave = useCallback((tasks: Task[]) => {
    if (subtaskEditorRow) {
      gridRef.current?.dispatchAdvanced(subtaskEditorRow._localId, { subtasks: tasks });
    }
    setSubtaskEditorOpen(false);
  }, [subtaskEditorRow]);

  const panelExistingTasks = panelRow
    ? (gridRef.current?.getRows() ?? [])
        .slice(0, panelRowIdx)
        .filter((r) => r.name.trim())
        .map((r, i) => toPanelTask(r, i + 1))
    : [];

  // Numbered after the named rows above, as saved: a row with no name has no number.
  const panelLaterTasks = panelRow
    ? (gridRef.current?.getRows() ?? [])
        .slice(panelRowIdx + 1)
        .filter((r) => r.name.trim())
        .map((r, i) => toPanelTask(r, panelExistingTasks.length + (panelRow.name.trim() ? 2 : 1) + i))
    : [];

  // The approval template's value source may be the task being configured, whose own
  // submitted value is read after the maker answers. Later tasks stay excluded: they
  // have no value yet at the moment the approval notification goes out.
  const panelApprovalSourceTasks = panelRow
    ? (gridRef.current?.getRows() ?? [])
        .slice(0, panelRowIdx + 1)
        .filter((r) => r.name.trim())
        .map((r, i) => toPanelTask(r, i + 1))
    : [];

  const linkTasks = isLinksOpen
    ? (gridRef.current?.getRows() ?? []).filter((r) => r.name.trim()).map((r, i) => toPanelTask(r, i + 1))
    : [];

  return (
    <Box
      p={6}
      rounded="md"
      shadow="lg"
      bg="bg-surface"
      bgGradient={cardGradient}
    >
      <VStack spacing={6} align="stretch">
        <FormField
          label="Title"
          name="title"
          register={form.register}
          error={form.formState.errors.title}
          required
          placeholder="Enter workflow title"
          maxLength={55}
          testId="wfv4-title-input"
        />

        <FormField
          label="Template Type"
          name="template"
          type="select"
          options={templateOptions}
          register={form.register}
          error={form.formState.errors.template}
          required
          testId="wfv4-template-select"
        />

        <FormField
          label="Task Lifecycle"
          name="task_lifecycle"
          type="select"
          options={lifecycleOptions}
          register={form.register}
          error={form.formState.errors.task_lifecycle}
          required
          description="Daily: tasks expire within 24 hours. Long Running: tasks can span days or weeks. Weekly/Monthly Recurring: tasks auto-reset each week or month."
          testId="wfv4-lifecycle-select"
        />

        {watchLifecycle === 'long_running' && (
          <FormField
            label="TTL Unit"
            name="ttl_unit"
            type="select"
            options={ttlUnitOptions}
            register={form.register}
            error={form.formState.errors.ttl_unit}
            required
            testId="wfv4-ttl-unit-select"
          />
        )}

        {(watchLifecycle === 'daily' || watchLifecycle === 'long_running') && (
          <FormField
            label={`Task Expiry Time in ${watchLifecycle === 'long_running' && watchTtlUnit === 'days' ? 'Days' : 'Hours'}`}
            name="ttl"
            type="number"
            register={form.register}
            error={form.formState.errors.ttl}
            required
            min={1}
            max={watchLifecycle === 'long_running' ? (watchTtlUnit === 'days' ? 365 : 8760) : 72}
            testId="wfv4-ttl-input"
          />
        )}

        {/* Task Grid */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={4} color="text-primary">
            Tasks
          </Text>
          <Divider mb={4} borderColor="border-default" />
          <WorkflowTaskGrid
            ref={gridRef}
            template={watchTemplate}
            chatbotLifecycle={watchLifecycle}
            initialTasks={initialData?.tasks ?? state?.copiedTasks ?? initialGridTasks}
            onValidRows={handleGridValidRows}
            onValidationError={handleGridValidationError}
            validateTriggerId={VALIDATE_TRIGGER_ID}
            onOpenConfigPanel={handleOpenConfigPanel}
            onOpenSubtaskEditor={handleOpenSubtaskEditor}
            onRowsChange={handleRowsChange}
            highlightTaskIds={highlightTaskIds}
            toolbarActions={taskToolbarActions}
          />
        </Box>

        {/* Linked Workflows (Form template only) */}
        {watchTemplate === 'form' && (
          <Box>
            <Text fontSize="lg" fontWeight="semibold" mb={4} color="text-primary">
              Linked Workflows
            </Text>
            <Divider mb={4} borderColor="border-default" />
            <Text fontSize="sm" color="text-secondary" mb={4}>
              Configure links to other workflows for dynamic STARTNEW targeting.
            </Text>

            {chatbotCode && orgId && (
              <>
                {isLoadingLinks ? (
                  <Skeleton height="32px" width="200px" mb={4} />
                ) : currentLinksCount > 0 ? (
                  <Box mb={4}>
                    <Text fontSize="sm" fontWeight="medium" color="text-primary" mb={2}>
                      Current Links ({currentLinksCount}):
                    </Text>
                    <Wrap spacing={2}>
                      {Object.entries(linksData?.links || {}).map(([key, value]) => (
                        <WrapItem key={key}>
                          <Badge colorScheme="brand" variant="subtle" px={3} py={1} borderRadius="full" fontSize="xs">
                            {key} → {getTargetFromLink(value)}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="text-muted" mb={4} fontStyle="italic">
                    No linked workflows configured.
                  </Text>
                )}
                <Button leftIcon={<MdLink />} onClick={onLinksOpen} variant="outline" colorScheme="brand" size="sm">
                  Manage Linked Workflows
                </Button>
              </>
            )}

            {!chatbotCode && (
              <>
                {Object.keys(pendingLinks).length > 0 ? (
                  <Box mb={4}>
                    <Text fontSize="sm" fontWeight="medium" color="text-primary" mb={2}>
                      Pending Links ({Object.keys(pendingLinks).length}):
                    </Text>
                    <Wrap spacing={2}>
                      {Object.entries(pendingLinks).map(([key, value]) => (
                        <WrapItem key={key}>
                          <Badge
                            colorScheme="brand" variant="subtle" px={3} py={1} borderRadius="full" fontSize="xs"
                            cursor="pointer"
                            onClick={() => { const nl = { ...pendingLinks }; delete nl[key]; setPendingLinks(nl); }}
                            title="Click to remove"
                          >
                            {key} → {getTargetFromLink(value)} ✕
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                ) : (
                  <Text fontSize="sm" color="text-muted" mb={4} fontStyle="italic">
                    No linked workflows configured. Links will be saved after workflow creation.
                  </Text>
                )}
                <Button leftIcon={<MdLink />} onClick={onLinksOpen} variant="outline" colorScheme="brand" size="sm">
                  Add Linked Workflows
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Bot Tags */}
        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb={4} color="text-primary">
            Bot Tags
          </Text>
          <Divider mb={4} borderColor="border-default" />
          {tags.map((tag, index) => (
            <HStack key={index} spacing={4} mb={4}>
              <FormField
                label="Key"
                name={`tags.${index}.key`}
                register={form.register}
                error={form.formState.errors.tags?.[index]?.key}
                required
                placeholder="Enter tag key"
                value={tag.key}
                onChange={(value) => handleTagChange(index, 'key', value)}
              />
              <FormField
                label="Value"
                name={`tags.${index}.value`}
                register={form.register}
                error={form.formState.errors.tags?.[index]?.value}
                required
                placeholder="Enter tag value"
                value={tag.value}
                onChange={(value) => handleTagChange(index, 'value', value)}
              />
              <IconButton
                aria-label="Remove tag"
                icon={<MdDelete />}
                onClick={() => handleRemoveTag(index)}
                alignSelf="flex-end"
                mb={3}
                variant="ghost"
                colorScheme="red"
              />
            </HStack>
          ))}
          <Button leftIcon={<MdAdd />} onClick={handleAddTag} mb={6} variant="ghost" size="sm">
            Add Tag
          </Button>
        </Box>

        <Button
          colorScheme="brand"
          isLoading={isLoading}
          loadingText={chatbotCode ? 'Updating...' : 'Creating...'}
          onClick={handleSubmitClick}
          data-testid="wfv4-submit-btn"
        >
          {/* Editing has a code; a create can start from initial data (an AI draft) */}
          {chatbotCode ? 'Update' : 'Create'} Workflow
        </Button>
      </VStack>

      {/* Validation error dialog */}
      <AlertDialog isOpen={isErrorDialogOpen} leastDestructiveRef={cancelRef} onClose={onErrorDialogClose}>
        <AlertDialogOverlay>
          <AlertDialogContent bg="bg-surface">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="text-primary">
              Form Validation Errors
            </AlertDialogHeader>
            <AlertDialogBody>
              {gridErrors > 0 && (
                <Text color="red.500" mb={2} fontSize="sm">
                  {gridErrors} task row{gridErrors !== 1 ? 's' : ''} have errors — fix highlighted rows in the task grid.
                </Text>
              )}
              <List spacing={3}>
                {validationErrors.map((error, index) => (
                  <ListItem key={index} display="flex" alignItems="center" color="red.500">
                    <ListIcon as={MdError} color="red.500" />
                    {error}
                  </ListItem>
                ))}
              </List>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onErrorDialogClose} colorScheme="brand">Close</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {orgId && (
        <LinksManager
          isOpen={isLinksOpen}
          onClose={onLinksClose}
          chatbotCode={chatbotCode}
          chatbotTitle={form.watch('title') || 'Workflow'}
          orgId={orgId}
          initialLinks={!chatbotCode ? pendingLinks : undefined}
          onLinksChange={!chatbotCode ? setPendingLinks : undefined}
          defaultTargetKey={defaultTargetKey}
          tasks={linkTasks}
        />
      )}

      <TaskConfigPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        row={panelRow}
        rowIdx={panelRowIdx}
        onSave={handlePanelSave}
        existingTasks={panelExistingTasks}
        laterTasks={panelLaterTasks}
        approvalSourceTasks={panelApprovalSourceTasks}
        chatbotLifecycle={watchLifecycle}
        availableWorkflows={availableWorkflows}
        flowsEnabled={flowsEnabled}
      />

      <SubtaskEditorModal
        isOpen={subtaskEditorOpen}
        onClose={() => setSubtaskEditorOpen(false)}
        initialTasks={subtaskEditorRow?.subtasks ?? []}
        onSave={handleSubtaskEditorSave}
        parentTaskName={subtaskEditorRow?.name}
      />
    </Box>
  );
};
